import React, { useState, useEffect } from 'react';
import styles from './ImportConfirmationModal.module.css';

const ImportConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  onImportOnly, 
  isPremiumUser, 
  currentComparisons, 
  maxComparisons,
  guestSessions = []
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState({});

  // Initialize selected sessions when guestSessions changes
  useEffect(() => {
    const initialSelection = guestSessions.reduce((acc, session) => {
      acc[session.id] = true; // All selected by default
      return acc;
    }, {});
    setSelectedSessions(initialSelection);
  }, [guestSessions]);

  if (!isOpen) return null;

  // Prevent rendering if guestSessions is not yet available
  if (!guestSessions || guestSessions.length === 0) {
    return null;
  }

  const handleSessionToggle = (sessionId) => {
    setSelectedSessions(prev => ({
      ...prev,
      [sessionId]: !prev[sessionId]
    }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(selectedSessions).every(selected => selected);
    const newState = guestSessions.reduce((acc, session) => {
      acc[session.id] = !allSelected;
      return acc;
    }, {});
    setSelectedSessions(newState);
  };

  const handleConfirm = async () => {
    if (selectedCount === 0) {
      return;
    }
    
    setIsLoading(true);
    try {
      // Filter selected sessions and pass them to onConfirm
      const selectedSessionsList = guestSessions.filter(session => selectedSessions[session.id]);
      await onConfirm(selectedSessionsList);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportOnly = async () => {
    if (selectedCount === 0) {
      return;
    }
    
    setIsLoading(true);
    try {
      // Filter selected sessions and pass them to onImportOnly
      const selectedSessionsList = guestSessions.filter(session => selectedSessions[session.id]);
      await onImportOnly(selectedSessionsList);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Unknown date';
    }
  };

  const selectedCount = Object.values(selectedSessions).filter(Boolean).length;
  const allSelected = selectedCount === guestSessions.length && guestSessions.length > 0;
  const hasReachedLimit = currentComparisons >= maxComparisons;
  const remainingComparisons = Math.max(0, maxComparisons - currentComparisons);

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>Import Guest Projects</h2>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.projectInfo}>
            <p>
              You have <strong>{guestSessions.length}</strong> guest project{guestSessions.length !== 1 ? 's' : ''} ready to import.
            </p>
          </div>

          {/* Projects List with Selection */}
          {guestSessions.length > 0 && (
            <div className={styles.projectsList}>
              <div className={styles.listHeader}>
                <div className={styles.selectAllContainer}>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={handleSelectAll}
                    />
                    <span className={styles.checkmark}></span>
                    Select All ({guestSessions.length} projects)
                  </label>
                </div>
                <div className={styles.selectedCount}>
                  {selectedCount} selected
                </div>
              </div>
              
              <div className={styles.projectsGrid}>
                {guestSessions.map((session) => (
                  <div 
                    key={session.id} 
                    className={`${styles.projectCard} ${selectedSessions[session.id] ? styles.selected : ''}`}
                    onClick={() => handleSessionToggle(session.id)}
                  >
                    <div className={styles.cardHeader}>
                      <div className={styles.checkbox}>
                        <input
                          type="checkbox"
                          checked={selectedSessions[session.id] || false}
                          onChange={() => {}} // Handled by card click
                          onClick={(e) => e.stopPropagation()} // Prevent double toggle
                        />
                        <span className={styles.checkmark}></span>
                      </div>
                      <div className={styles.projectIcon}>
                        <i className="fas fa-file-contract"></i>
                      </div>
                    </div>
                    
                    <div className={styles.cardContent}>
                      <h4>{session.projectName || 'Untitled Project'}</h4>
                      <p className={styles.projectType}>{session.projectType || 'Project'}</p>
                      <div className={styles.projectMeta}>
                        <div className={styles.metaItem}>
                          <i className="fas fa-users"></i>
                          <span>
                            {session.contractorCount || (session.data?.contractors || []).length || 0} contractor{(session.contractorCount || (session.data?.contractors || []).length || 0) !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className={styles.metaItem}>
                          <i className="fas fa-clock"></i>
                          <span>{formatDate(session.createdAt)}</span>
                        </div>
                        {session.data?.project_budget && (
                          <div className={styles.metaItem}>
                            <i className="fas fa-dollar-sign"></i>
                            <span>${session.data.project_budget}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isPremiumUser ? (
            <div className={styles.premiumOptions}>
              <div className={styles.optionCard}>
                <h3>🚀 Import with Premium Analysis</h3>
                <p>
                  Import your selected projects and automatically run premium analysis to get:
                </p>
                <ul>
                  <li>Detailed contractor evaluations</li>
                  <li>Expert clarification questions</li>
                  <li>Comprehensive recommendations</li>
                  <li>Risk assessment and insights</li>
                </ul>
                {hasReachedLimit ? (
                  <div className={styles.limitWarning}>
                    <p>⚠️ You've reached your comparison limit ({currentComparisons}/{maxComparisons})</p>
                    <p>You can still run premium analysis, but you'll need to upgrade your plan for future comparisons.</p>
                  </div>
                ) : (
                  <p className={styles.comparisonInfo}>
                    This will use {selectedCount} of your {remainingComparisons} remaining comparisons.
                  </p>
                )}
                <button 
                  className={`${styles.actionButton} ${styles.primaryButton}`}
                  onClick={handleConfirm}
                  disabled={isLoading || selectedCount === 0}
                >
                  {isLoading ? 'Processing...' : `Import ${selectedCount} with Premium Analysis`}
                </button>
              </div>

              <div className={styles.optionCard}>
                <h3>📁 Import Only</h3>
                <p>
                  Import your selected projects without running premium analysis. You can run analysis later when you're ready.
                </p>
                <p className={styles.comparisonInfo}>
                  This won't use any of your comparisons.
                </p>
                <button 
                  className={`${styles.actionButton} ${styles.secondaryButton}`}
                  onClick={handleImportOnly}
                  disabled={isLoading || selectedCount === 0}
                >
                  {isLoading ? 'Importing...' : `Import ${selectedCount} Only`}
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.freeUserOption}>
              <p>
                Your selected projects will be imported without premium analysis. 
                Upgrade to a premium plan to unlock detailed analysis features.
              </p>
              <button 
                className={`${styles.actionButton} ${styles.primaryButton}`}
                onClick={handleImportOnly}
                disabled={isLoading || selectedCount === 0}
              >
                {isLoading ? 'Importing...' : `Import ${selectedCount} Projects`}
              </button>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button 
            className={styles.cancelButton}
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportConfirmationModal; 